// @ts-check
// Ações que os fluxos herdam das automações (spec 2.0.0, fluxos.md seção 20.7; contrato 09 de fluxos, seções 2.9 e 17):
// "Alterar campo", "Criar tarefa", "Avisar a equipe", "Enviar para integração" e "Requisição HTTP" usam o mesmo
// formulário das automações (AutomationActionFields), com o card como registro e as variáveis do caminho (card, contato,
// instituição e as respostas das requisições HTTP anteriores). "Alterar campo" não oferece Etapa nem Etiquetas, que
// têm ações próprias.
import { html } from 'htm/preact';
import { useMemo } from 'preact/hooks';
import { AutomationActionFields } from '../../automations/automation-actions-editor.js';
import { workspaceMembersState } from '../../../data/workspace-members.js';
import { defaultNodeConfig, indexFlowNodes, listNodesOnPathTo, FLOW_UPDATE_FIELD_BLOCKED_FIELDS } from '../../../../../shared/flows/flow-definition.js';
import { cardObject } from '../flow-lookups.js';
import { flowProblemText } from '../flow-ui.js';
import { t } from '../../../i18n/index.js';

/**
 * @typedef {import('../flow-node-panel.js').NodeFormProps} NodeFormProps
 * @typedef {import('../../../../../shared/flows/flow-definition.js').FlowNode} FlowNode
 */

// gatilho de registro: o formulário mostra as variáveis do card e do contato
const FLOW_TRIGGER_TYPE = 'RECORD_UPDATED';

/** Nó do fluxo no formato de ação das automações. @param {FlowNode} node */
export const nodeAsAutomationAction = (node) => /** @type {any} */ ({ ...node.config, type: node.type, id: node.id });

/**
 * Configuração do nó a partir da ação editada: só as chaves do tipo.
 * @param {string} type @param {Record<string, any>} action
 */
export function automationActionAsConfig(type, action) {
  const keys = Object.keys(defaultNodeConfig(/** @type {any} */ (type)));
  return Object.fromEntries(keys.map((key) => [key, action[key] ?? null]));
}

/** @param {NodeFormProps} props */
export function AutomationActionForm({ node, definition, problems, onReplaceConfig }) {
  const object = cardObject();
  const objectMetadata = useMemo(() => {
    if (!object || node.type !== 'UPDATE_FIELD') return object;
    return { ...object, fields: object.fields.filter((field) => !FLOW_UPDATE_FIELD_BLOCKED_FIELDS.includes(field.name)) };
  }, [object, node.type]);
  // as requisições HTTP antes deste passo no caminho viram grupos de variáveis
  const pathActions = useMemo(() => listNodesOnPathTo(indexFlowNodes(definition), node.id)
    .filter((candidate) => candidate.id === node.id || candidate.type === 'HTTP_REQUEST')
    .map(nodeAsAutomationAction), [definition, node.id]);
  const action = pathActions.find((candidate) => candidate.id === node.id) ?? nodeAsAutomationAction(node);
  const context = /** @type {any} */ ({
    scope: { objectMetadata, triggerType: FLOW_TRIGGER_TYPE, triggerSettings: {}, actions: pathActions },
    members: workspaceMembersState.value ?? [], templates: null, disabled: false, triggerType: FLOW_TRIGGER_TYPE, triggerSettings: {}, whatsappChannels: [],
  });
  const nodeProblems = problems.filter((problem) => problem.code !== 'NODE_INCOMPLETE');
  const isIncomplete = problems.some((problem) => problem.code === 'NODE_INCOMPLETE');
  return html`<div class="flow-node-form flow-automation-form" data-testid=${`flow-${node.type.toLowerCase().replace(/_/g, '-')}-form`}>
    <${AutomationActionFields} action=${action} objectMetadata=${objectMetadata} context=${context} depth=${0}
      onChange=${(/** @type {Record<string, any>} */ next) => onReplaceConfig(automationActionAsConfig(node.type, next))} />
    ${isIncomplete ? html`<p class="flow-field-error" role="alert">${t('flows.forms.automation.incomplete')}</p>` : null}
    ${nodeProblems.map((problem) => html`<p key=${problem.code} class="flow-field-error" role="alert">${flowProblemText(problem)}</p>`)}
  </div>`;
}
